/* Billing / Pricing / Payment status pages. */
import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, ArrowRight, Zap, CheckCircle2, XCircle } from "lucide-react";
import { logger } from "@/lib/logger";

const PLAN_META = {
  creator_monthly: { name: "Creator", features: ["Unlimited projects", "AI Writer + Script Doctor", "1,000 AI credits / month", "Character Lab & Relationships", "Priority AI queue"], highlight: true },
  creator_yearly: { name: "Creator (Yearly)", features: ["All Creator features", "Save 20% vs monthly", "1,000 AI credits / month"], highlight: true },
  studio_monthly: { name: "Studio", features: ["Everything in Creator", "AI Director + Storyboard Studio", "5,000 AI credits / month", "Production dashboard", "Advanced exports (FDX)", "Team seats"], highlight: false },
  studio_yearly: { name: "Studio (Yearly)", features: ["All Studio features", "Save 20% vs monthly", "5,000 AI credits / month"], highlight: false },
  credits_small: { name: "500 credits", features: ["One-time top-up"], pack: true },
  credits_medium: { name: "2,000 credits", features: ["One-time top-up", "Best for regular users"], pack: true, highlight: true },
  credits_large: { name: "6,000 credits", features: ["One-time top-up", "Best value"], pack: true },
};


export function BillingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);
  const [billing, setBilling] = React.useState("month");

  React.useEffect(() => {
    api.get("/payments/plans").then((r) => setPlans(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const startCheckout = async (lookup) => {
    setBusy(lookup);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const subs = plans.filter((p) => p.interval === billing);
  const packs = plans.filter((p) => !p.interval);

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Billing</div>
        <h1 className="font-editorial text-4xl mb-3">Your plan &amp; credits</h1>
        <div className="text-sm text-[#A1A1AA] mb-8">Current plan: <span className="text-[#D4AF37] uppercase" data-testid="current-plan">{user?.plan}</span> · Credits: <span className="text-[#FFFFF0] font-mono" data-testid="current-credits">{user?.credits}</span></div>

        <div className="inline-flex border border-[#27272A] mb-6">
          <button onClick={() => setBilling("month")} className={`px-6 py-2 text-sm ${billing === "month" ? "bg-[#D4AF37] text-[#0A0A0A]" : "text-[#A1A1AA]"}`} data-testid="billing-monthly-toggle">Monthly</button>
          <button onClick={() => setBilling("year")} className={`px-6 py-2 text-sm ${billing === "year" ? "bg-[#D4AF37] text-[#0A0A0A]" : "text-[#A1A1AA]"}`} data-testid="billing-yearly-toggle">Yearly · save 20%</button>
        </div>

        {loading ? <div className="text-[#A1A1AA]">Loading plans…</div> :
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1E1E1E] mb-16">
              <PlanCard
                p={{ lookup_key: "free", amount: 0, interval: billing, product_name: "Free" }}
                meta={{ name: "Free", features: ["1 project", "50 AI credits/month", "Basic editor", "Community access"] }}
                current={user?.plan === "free"}
                onChoose={() => toast.info("You are on the free plan.")}
              />
              {subs.map((p) => (
                <PlanCard
                  key={p.lookup_key}
                  p={p}
                  meta={PLAN_META[p.lookup_key] || {}}
                  current={
                    (user?.plan === "creator" && p.lookup_key.startsWith("creator")) ||
                    (user?.plan === "studio" && p.lookup_key.startsWith("studio"))
                  }
                  onChoose={() => startCheckout(p.lookup_key)}
                  busy={busy === p.lookup_key}
                />
              ))}
            </div>

            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">Credit packs</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1E1E1E]">
              {packs.map((p) => (
                <PackCard key={p.lookup_key} p={p} meta={PLAN_META[p.lookup_key] || {}}
                  onBuy={() => startCheckout(p.lookup_key)} busy={busy === p.lookup_key} />
              ))}
            </div>
          </>
        }
      </div>
    </DashboardLayout>
  );
}

function PlanCard({ p, meta, current, onChoose, busy }) {
  return (
    <div className={`p-8 flex flex-col ${meta.highlight ? "bg-[#141414] border-t-2 border-[#D4AF37]" : "bg-[#0A0A0A]"}`} data-testid={`plan-card-${p.lookup_key}`}>
      <div className="uppercase text-[11px] tracking-widest text-[#71717A]">{meta.name}</div>
      <div className="mt-6 flex items-baseline gap-2">
        <div className="font-editorial text-5xl">${p.amount}</div>
        {p.interval && <div className="text-xs text-[#71717A]">/ {p.interval}</div>}
      </div>
      <ul className="mt-6 space-y-3 text-sm flex-1">
        {(meta.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-[#E4E4E7]">
            <Check size={14} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} /> {f}
          </li>
        ))}
      </ul>
      <button onClick={onChoose} disabled={busy || current}
        className={`mt-8 py-3 text-center text-sm font-medium transition-colors ${planButtonClass(current, meta.highlight)} disabled:opacity-70`}
        data-testid={`plan-choose-${p.lookup_key}`}>
        <PlanButtonLabel busy={busy} current={current} />
      </button>
    </div>
  );
}

function planButtonClass(current, highlight) {
  if (current) return "bg-[#27272A] text-[#A1A1AA]";
  if (highlight) return "bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F59E0B]";
  return "border border-[#27272A] hover:border-[#A1A1AA] text-[#FFFFF0]";
}

function PlanButtonLabel({ busy, current }) {
  if (busy) return <Loader2 size={14} className="inline animate-spin" />;
  return current ? "Current plan" : "Upgrade";
}

function PackCard({ p, meta, onBuy, busy }) {
  return (
    <div className={`p-6 flex flex-col ${meta.highlight ? "bg-[#141414] border-t-2 border-[#D4AF37]" : "bg-[#0A0A0A]"}`} data-testid={`pack-card-${p.lookup_key}`}>
      <div className="flex items-center gap-2">
        <Zap size={14} className="text-[#D4AF37]" />
        <div className="uppercase text-[11px] tracking-widest text-[#71717A]">{meta.name}</div>
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-editorial text-4xl">${p.amount}</div>
      </div>
      <ul className="mt-3 space-y-1 text-xs flex-1">
        {(meta.features || []).map((f) => (
          <li key={f} className="text-[#A1A1AA]">{f}</li>
        ))}
      </ul>
      <button onClick={onBuy} disabled={busy}
        className="mt-5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] py-2 text-sm inline-flex items-center justify-center gap-2 transition-colors"
        data-testid={`pack-buy-${p.lookup_key}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <>Buy <ArrowRight size={12} /></>}
      </button>
    </div>
  );
}


export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = React.useState("polling");
  const { refresh } = useAuth();

  React.useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (err) {
        logger.warn("payment status poll error", err);
      }
      attempts++;
      if (attempts < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFF0] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="animate-spin mx-auto text-[#D4AF37]" />
            <div className="font-editorial text-3xl mt-6">Confirming payment…</div>
            <p className="text-sm text-[#A1A1AA] mt-2">Give us a moment. This usually resolves in a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-[#D4AF37]" strokeWidth={1.5} />
            <div className="font-editorial text-4xl mt-6">Welcome to the upgrade.</div>
            <p className="text-sm text-[#A1A1AA] mt-3">Your plan and credits are ready.</p>
            <button onClick={() => navigate("/app")} className="mt-8 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 text-sm font-medium hover:bg-[#F59E0B] transition-colors" data-testid="payment-success-continue">
              Back to workspace
            </button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle size={48} className="mx-auto text-[#7F1D1D]" strokeWidth={1.5} />
            <div className="font-editorial text-3xl mt-6">Something didn&apos;t clear.</div>
            <p className="text-sm text-[#A1A1AA] mt-2">Try again or contact support.</p>
            <button onClick={() => navigate("/app/billing")} className="mt-6 border border-[#27272A] px-6 py-3 text-sm">Back to billing</button>
          </>
        )}
      </div>
    </div>
  );
}


export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFF0] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <XCircle size={48} className="mx-auto text-[#71717A]" strokeWidth={1.5} />
        <div className="font-editorial text-3xl mt-6">Payment cancelled.</div>
        <p className="text-sm text-[#A1A1AA] mt-2">No charge was made. You can try again anytime.</p>
        <button onClick={() => navigate("/app/billing")} className="mt-8 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 text-sm font-medium">Back to billing</button>
      </div>
    </div>
  );
}
